// src/context/CartContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { trackAddToCart } from '@/lib/gtm.js';

const CartContext = createContext(null);

const STORAGE_KEY = 'hublu_cart';

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // storage full — ignore
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadCart());

  // Persist on every change
  useEffect(() => { saveCart(items); }, [items]);

  const addToCart = useCallback((product, qty = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        toast.success(`${product.title} quantity updated`);
        return prev.map(i =>
          i.id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      toast.success(`${product.title} added to cart`);
      return [...prev, {
        id:            product.id,
        title:         product.title,
        price:         product.price,
        compare_price: product.compare_price ?? null,
        image:         product.images?.[0]?.src || product.featured_image || null,
        quantity:      qty,
      }];
    });
    trackAddToCart({ ...product, quantity: qty });
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity < 1) return;
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
  }, []);

  const removeFromCart = useCallback((id) => {
    setItems(prev => prev.filter(i => i.id !== id));
    toast.info('Item removed from cart');
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const subtotal     = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const deliveryCost = subtotal >= 1000 ? 0 : (items.length > 0 ? 99 : 0);
  const total        = subtotal + deliveryCost;
  const itemCount    = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      subtotal,
      deliveryCost,
      total,
      itemCount,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}